<!-- Navigation Header Desktop -->
<div class="bg-[#f5f5fa]  md:fixed top-0 left-0 w-full md:z-50">
  <!-- Desktop Navigation (lg screens and up: 1024px+) -->
  <nav *ngIf="!(isMobile$ | async)" class="relative z-50 hidden lg:block">
    <div class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      <div class="flex items-center justify-between h-14 sm:h-16 md:h-16 lg:h-16 bg-white lg:rounded-full mt-2 sm:mt-4 shadow-sm">
        <!-- Logo -->
        <div routerLink="/" class="animate-fade-in-down pl-4 sm:pl-6 lg:pl-8 xl:pl-10 cursor-pointer transition-transform duration-300 hover:scale-[1.02]">
          <div class="logo-container">
            <img src="assets/icons/mobile_logo.svg" alt="Company Logo" class="" />
          </div>
        </div>

        <!-- Desktop Menu -->
        <div class="hidden lg:flex items-center gap-6 xl:gap-8 2xl:gap-12">
          <a routerLink="products" (click)="onFeatureClick()"
            class="nav-link text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors duration-200 relative">
            Products
          </a>

          <a [href]="baseUrl + 'docs'" target="_blank" rel="noopener noreferrer" (click)="onFeatureClick()"
            class="nav-link text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors duration-200 relative">
            Developers
          </a>

          <!-- Company Dropdown (click to toggle) -->
          <div class="relative">
            <!-- Trigger -->
            <button type="button" (click)="toggleDesktopMenu(); $event.stopPropagation()"
              class="nav-link text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors duration-200 flex items-center gap-1.5 cursor-pointer select-none relative group"
              [attr.aria-expanded]="isDesktopMenuOpen()" 
              aria-haspopup="true"
              aria-controls="company-dropdown">
              Company
              <svg class="w-4 h-4 transition-transform duration-300 ease-out" 
                [class.rotate-180]="isDesktopMenuOpen()"
                aria-hidden="true" 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                fill="none"
                viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m19 9-7 7-7-7" />
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <div id="company-dropdown"
              *ngIf="isDesktopMenuOpen()" 
              class="dropdown-menu absolute top-full right-0 mt-2 w-80 xl:w-96 bg-white rounded-xl border border-gray-200 shadow-2xl
                      origin-top-right
                      animate-dropdown-enter"
              (click)="$event.stopPropagation()"
              role="menu">
              <div class="w-full space-y-1 p-3 xl:p-4">
                <div *ngFor="let feature of features; let i = index" 
                  class="flex cursor-pointer items-start gap-3 xl:gap-4 p-3 xl:p-4 rounded-xl bg-card hover:bg-gray-50 hover:shadow-md
                         transition-all duration-300 ease-out hover:-translate-y-0.5 animate-fade-in-up"
                  [style.animation-delay.ms]="feature.delay"
                  role="menuitem"
                  (click)="onFeatureClick()"
                  [routerLink]="feature.route">
                  <div class="flex-shrink-0 w-10 h-10 xl:w-12 xl:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    [class]="feature.iconBgColor">
                    <div [innerHTML]="feature.svg | safeHtml" class="w-6 h-6 xl:w-7 xl:h-7"></div>
                  </div>

                  <div class="flex-1 min-w-0">
                    <h3 class="font-semibold text-foreground mb-1 text-sm xl:text-base leading-tight">
                      {{ feature.title }}
                    </h3>
                    <p class="text-xs xl:text-sm text-muted-foreground leading-relaxed">
                      {{ feature.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Get Started Button (Desktop) -->
        <div class="hidden lg:block animate-slide-in-right pr-4 sm:pr-6 lg:pr-8 xl:pr-10">
          <a [href]="baseUrl + 'register-form'" class="inline-block">
            <button
              class="btn-get-started bg-black text-white px-6 md:px-8 lg:px-10 xl:px-12 py-2.5 xl:py-3 h-12 lg:h-13 xl:h-14 rounded-full text-xs md:text-sm xl:text-sm font-semibold whitespace-nowrap hover:bg-gray-900 transition-all duration-300 ease-out transform hover:-translate-y-0.5 active:translate-y-0">
              Get Started
            </button>
          </a>
        </div>
      </div>
    </div>

    <div class="border-b border-gray-100 mt-2 sm:mt-4"></div>
  </nav>

  <!-- Mobile/Tablet Navigation (below 1024px) -->
  <div *ngIf="isMobile$ | async" class="relative z-50">
    <nav class="fixed top-0 left-0 w-full z-50 bg-white shadow-sm transition-shadow duration-300"
      [class.shadow-lg]="isMobileMenuOpen()">
      <div class="w-full">
        <div class="px-3 xs:px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between"
          [ngClass]="{ 'bg-[#f5f5fa]': isMobileMenuOpen() }">

          <!-- Logo -->
          <div class="animate-fade-in-down flex-shrink-0">
            <div routerLink="/" 
              class="logo-container cursor-pointer w-24 xs:w-28 sm:w-32 transition-transform duration-200 active:scale-95"
              (click)="isMobileMenuOpen() && toggleMobileMenu()">
              <img src="assets/icons/mobile_logo.svg" alt="Company Logo" class="w-full h-auto max-h-10" />
            </div>
          </div>
          
          <!-- Mobile Menu Toggle -->
          <button (click)="toggleMobileMenu()" 
            type="button" 
            aria-label="Toggle navigation menu"
            [attr.aria-expanded]="isMobileMenuOpen()"
            aria-controls="mobile-menu-panel"
            class="flex-shrink-0 p-2.5 sm:p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 touch-manipulation focus:outline-none focus:ring-2 focus:ring-gray-300">
            <svg *ngIf="!isMobileMenuOpen()" class="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg *ngIf="isMobileMenuOpen()" class="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu Overlay -->
      <div *ngIf="isMobileMenuOpen()" 
        class="fixed inset-0 top-14 sm:top-16 bg-black/30 backdrop-blur-sm z-40 animate-fade-in"
        (click)="toggleMobileMenu()"
        aria-hidden="true"></div>

      <!-- Mobile Menu Panel -->
      <div id="mobile-menu-panel"
        *ngIf="isMobileMenuOpen()"
        class="fixed top-14 sm:top-16 left-0 right-0 bg-white shadow-xl rounded-b-2xl overflow-y-auto max-h-[calc(100vh-3.5rem)] sm:max-h-[calc(100vh-4rem)] z-50 animate-slide-down focus:outline-none"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu">
        <div class="px-3 xs:px-4 sm:px-6 py-4 sm:py-6">

          <!-- Products Link -->
          <a (click)="onFeatureClick()" 
            routerLink="products"
            class="block w-full text-left px-3 xs:px-4 py-3.5 sm:py-4 text-foreground font-medium hover:bg-gray-50 rounded-xl transition-colors duration-200 touch-manipulation focus:outline-none focus:ring-2 focus:ring-gray-200">
            Products
          </a>

          <!-- Developers Link -->
          <a [href]="baseUrl + 'docs'" 
            target="_blank" 
            rel="noopener noreferrer" 
            (click)="onFeatureClick()"
            class="block w-full text-left px-3 xs:px-4 py-3.5 sm:py-4 text-foreground font-medium hover:bg-gray-50 rounded-xl transition-colors duration-200 touch-manipulation focus:outline-none focus:ring-2 focus:ring-gray-200">
            Developers
          </a>

          <!-- Company Dropdown -->
          <div class="mt-1">
            <button (click)="toggleCompanyDropdown()" 
              type="button"
              [attr.aria-expanded]="isCompanyOpen()"
              aria-controls="company-mobile-dropdown"
              class="w-full flex items-center justify-between px-3 xs:px-4 py-3.5 sm:py-4 text-foreground font-medium hover:bg-gray-50 rounded-xl transition-colors duration-200 touch-manipulation focus:outline-none focus:ring-2 focus:ring-gray-200">
              <span>Company</span>
              <svg [class.rotate-180]="isCompanyOpen()" 
                class="w-5 h-5 transition-transform duration-300 ease-out"
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor"
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <!-- Company Dropdown Content -->
            <div id="company-mobile-dropdown"
              [ngClass]="{
                 'max-h-[600px] opacity-100 mt-2': isCompanyOpen(),
                 'max-h-0 opacity-0': !isCompanyOpen()
               }" 
               class="overflow-hidden transition-all duration-300 ease-in-out">
              <div class="pt-1 pb-1 space-y-1.5 sm:space-y-2">
                <button *ngFor="let item of features" 
                  type="button"
                  (click)="onFeatureClick()"
                  [routerLink]="item.route"
                  class="w-full flex items-start gap-3 sm:gap-4 p-3 xs:p-3.5 sm:p-4 text-left hover:bg-gray-50 rounded-xl transition-all duration-200 hover:translate-x-1 touch-manipulation focus:outline-none focus:ring-2 focus:ring-gray-200 group">
                  <!-- Icon -->
                  <div [class]="item.iconBgColor"
                    class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                    <div [innerHTML]="item.svg | safeHtml" class="scale-90 sm:scale-100 w-6 h-6 sm:w-7 sm:h-7"></div>
                  </div>

                  <!-- Content -->
                  <div class="flex-1 min-w-0 pt-1 sm:pt-1.5">
                    <h3 class="font-medium text-foreground text-sm sm:text-base leading-tight">
                      {{ item.title }}
                    </h3>
                    <p class="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 leading-relaxed">
                      {{ item.description }}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Get Started Button (Mobile) -->
          <div class="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
            <a [href]="baseUrl + 'register-form'" (click)="toggleMobileMenu()">
              <button
                class="w-full btn-get-started bg-black text-white px-6 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-900 transition-all duration-300 ease-out transform active:scale-[0.98] touch-manipulation focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                Get Started
              </button>
            </a>
          </div>
        </div>
      </div>

      <div class="border-b border-gray-100"></div>
    </nav>

    <!-- Spacer to prevent content from going under fixed nav -->
    <div class="h-14 sm:h-16"></div>
  </div>
</div>